import type { MindmapTree } from "./mindmapTree";
import { capTree } from "./mindmapTree";

/**
 * Pure database → tree builder for the auto-mindmap.
 *
 * A Notion database is fetched through the OFFICIAL API (`databases.query`), so
 * its rows are official-API page objects: each carries a `properties` map whose
 * values are typed (`{ type: "select" | "status" | "title", ... }`) — a
 * different shape from the unofficial page recordMap the page builder consumes.
 *
 * The builder turns those rows into the same `MindmapTree` the page pipeline
 * produces, so the existing layout / emitter / caps just work:
 *  - root      = the database (a live `notion` page-node opening the database)
 *  - a grouped map fans one branch per select/status option, rows beneath
 *  - a flat map hangs the rows straight off the root
 *
 * Grouping rule (spec, resolved in grill review): exactly one select/status
 * property → auto-use it; several → the caller picks; none → flat.
 */

export type GroupingType = "select" | "status";

export interface Grouping {
  propertyName: string;
  type: GroupingType;
}

/** A select/status property value on a row (only the fields we read). */
interface NotionRowProperty {
  type: string;
  select?: { name?: string } | null;
  status?: { name?: string } | null;
  title?: { plain_text?: string }[];
}

/** One database row as returned by the official `databases.query` API. */
export interface NotionDbRow {
  id: string;
  icon?: { type: string; [key: string]: string } | null;
  public_url?: string | null;
  properties: Record<string, NotionRowProperty>;
}

/** Minimal database identity the root node needs. */
export interface DatabaseMeta {
  id: string;
  title: string;
}

/** The branch a row falls under when its grouping value is empty. */
export const UNGROUPED_LABEL = "Ungrouped";

const GROUPING_TYPES: ReadonlySet<string> = new Set(["select", "status"]);

/**
 * The database's select/status properties, in first-seen order across the rows,
 * deduped by name — the properties a map could be grouped by. A row need not
 * carry every property, so we scan them all.
 */
export const candidateGroupings = (
  rows: readonly NotionDbRow[],
): Grouping[] => {
  const seen = new Set<string>();
  const out: Grouping[] = [];
  for (const row of rows) {
    for (const [name, prop] of Object.entries(row.properties ?? {})) {
      if (GROUPING_TYPES.has(prop?.type) && !seen.has(name)) {
        seen.add(name);
        out.push({ propertyName: name, type: prop.type as GroupingType });
      }
    }
  }
  return out;
};

/** The title text of a row, from its `title`-typed property (falls back). */
const rowTitle = (row: NotionDbRow): string => {
  for (const prop of Object.values(row.properties ?? {})) {
    if (prop?.type === "title") {
      const text = (prop.title ?? [])
        .map((segment) => segment?.plain_text ?? "")
        .join("");
      if (text) {
        return text;
      }
    }
  }
  return "Untitled";
};

/** The row's value for a grouping property, or null when unset/empty. */
const groupValue = (row: NotionDbRow, grouping: Grouping): string | null => {
  const prop = row.properties?.[grouping.propertyName];
  if (!prop) {
    return null;
  }
  const option = grouping.type === "select" ? prop.select : prop.status;
  return option?.name ?? null;
};

/**
 * A row → a live `notion` page-node that opens the row's page on click. A
 * row is expandable into its own page's skeleton, so `hasChildren` is
 * always set — a DB row always shows an expand chip.
 */
const rowNode = (row: NotionDbRow, blockId: string): MindmapTree => ({
  blockId,
  text: rowTitle(row),
  kind: "page",
  pageId: row.id,
  hasChildren: true,
  icon: row.icon ?? null,
  children: [],
});

export const buildDbMindmapTree = (
  databaseMeta: DatabaseMeta,
  rows: readonly NotionDbRow[],
  grouping?: Grouping,
): MindmapTree => {
  const root: MindmapTree = {
    blockId: databaseMeta.id,
    text: databaseMeta.title,
    // A database, NOT a page: its id is a data_source id, so the side peek must
    // never try to open it. `pageId` is kept for future use (deep links).
    kind: "database",
    pageId: databaseMeta.id,
    // Rows exist ⇒ the root is expandable. Without this the emitter's chip gate
    // (`hasChildren === true`) left every database map with no chips at all.
    hasChildren: rows.length > 0,
    children: [],
  };

  if (!grouping) {
    // Flat: rows hang straight off the root. Row blockId is its page id.
    root.children = rows.map((r) => rowNode(r, r.id));
    return capTree(root);
  }

  // Grouped: one branch per option, in first-seen order, "Ungrouped" last. A
  // row's blockId embeds its group so a group move on refresh reads as a
  // remove+add (the row's page id still drives what opens, via `pageId`).
  const branches = new Map<string, MindmapTree>();
  const order: string[] = [];

  const groupId = (label: string): string =>
    `${databaseMeta.id}::group::${label}`;

  for (const r of rows) {
    const label = groupValue(r, grouping) ?? UNGROUPED_LABEL;
    let branch = branches.get(label);
    if (!branch) {
      branch = {
        blockId: groupId(label),
        text: label,
        kind: "block",
        // A group always receives at least the row that created it.
        hasChildren: true,
        children: [],
      };
      branches.set(label, branch);
      order.push(label);
    }
    branch.children.push(rowNode(r, `${groupId(label)}::${r.id}`));
  }

  // Keep option branches in first-seen order but sink "Ungrouped" to the end.
  const ordered = order
    .filter((label) => label !== UNGROUPED_LABEL)
    .concat(order.includes(UNGROUPED_LABEL) ? [UNGROUPED_LABEL] : []);

  root.children = ordered.map((label) => branches.get(label)!);
  return capTree(root);
};
